import React, { useState } from "react";

/**
 * A carteira de custódia do cliente, uma seção por REDE: o endereço daquela rede e os
 * saldos que existem nela.
 *
 * ── Por que por rede, e não por ativo ──
 *
 * A tela mostrava um endereço só, pedido como "o endereço do USDT". Essa pergunta não
 * tem resposta única: a carteira nasce com endereço em seis ou sete redes, e na Tron
 * USDT, USDC e o TRX nativo chegam no MESMO endereço. Um endereço por ativo dava a
 * entender que eram depósitos diferentes, e escolher a rede no lugar do cliente é o
 * jeito de fazê-lo mandar cripto na cadeia errada — que é perda total.
 *
 * ── Rede com saldo zero aparece ──
 *
 * É exatamente onde ele vai depositar. Esconder as vazias esconderia a que ele está
 * procurando.
 *
 * ── O valor é mostrado COMO VEIO ──
 *
 * String, sem `toFixed` e sem `Number`: são casas decimais de cripto, e converter para
 * número perde as últimas. Saldo é o dado que o cliente confere contra o extrato.
 */

function shorten(value) {
  const text = String(value || "");
  if (text.length <= 22) return text;
  return `${text.slice(0, 10)}…${text.slice(-8)}`;
}

function Network({ network }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(network.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      // Sem área de transferência (contexto não seguro, permissão negada), o endereço
      // continua à vista para copiar à mão — o que não pode acontecer é a falha
      // silenciosa deixar o cliente achando que copiou.
      console.error("Não foi possível copiar o endereço:", err);
      setExpanded(true);
    }
  };

  const balances = network.balances || [];

  return (
    <div className="custody__network">
      <div className="custody__networkHead">
        <span className="custody__networkName">{network.label || network.network}</span>
        {balances.length === 0 ? (
          <span className="custody__empty">sem saldo nesta rede</span>
        ) : null}
      </div>

      {network.address ? (
        <div className="custody__addressRow">
          <span
            className="custody__address"
            title={network.address}
            onClick={() => setExpanded((previous) => !previous)}
          >
            {expanded ? network.address : shorten(network.address)}
          </span>
          <button type="button" className="custody__copy" onClick={copy}>
            {copied ? "copiado" : "copiar"}
          </button>
        </div>
      ) : (
        /*
          Rede com saldo e SEM endereço: o saldo é real e aparece, e o que falta é onde
          depositar. Dizer isso é melhor do que omitir a rede — omitir faria a soma da
          tela não fechar com o saldo da custódia.
        */
        <div className="custody__noAddress">
          Sem endereço de depósito nesta rede. Fale com o suporte para abrir um.
        </div>
      )}

      {balances.length > 0 ? (
        <table className="custody__balances">
          <tbody>
            {balances.map((balance) => (
              <tr key={balance.asset}>
                <td className="custody__symbol">{balance.symbol || balance.asset}</td>
                <td className="custody__value">{balance.value}</td>
                {Number(balance.frozenValue) > 0 ? (
                  <td className="custody__frozen" title="Saldo bloqueado">
                    {balance.frozenValue} bloqueado
                  </td>
                ) : (
                  <td />
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export default function CustodyWalletCard({ wallet, loading }) {
  const networks = wallet?.networks || [];

  return (
    <section className="card custody">
      <style>{`
        .custody__network { border-top: 1px solid var(--border); padding: 12px 0; }
        .custody__network:first-of-type { border-top: 0; }
        .custody__networkHead { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
        .custody__networkName { font-weight: bold; }
        .custody__empty { font-size: 11px; color: var(--muted); text-transform: uppercase; }
        .custody__addressRow { display: flex; align-items: center; gap: 8px; margin-top: 6px; flex-wrap: wrap; }
        .custody__address { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; overflow-wrap: anywhere; cursor: pointer; }
        .custody__copy { font-size: 11px; padding: 2px 8px; }
        .custody__noAddress { margin-top: 6px; font-size: 12px; color: #b42318; }
        .custody__balances { width: 100%; margin-top: 8px; border-collapse: collapse; }
        .custody__balances td { padding: 2px 0; font-size: 13px; vertical-align: baseline; }
        .custody__symbol { font-weight: bold; width: 80px; }
        .custody__value { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; overflow-wrap: anywhere; }
        .custody__frozen { font-size: 11px; color: var(--muted); text-align: right; }
      `}</style>

      <div className="card__title">Sua carteira de custódia</div>

      {loading ? (
        <span>Carregando…</span>
      ) : !wallet?.hasAccount ? (
        /*
          Sem carteira é estado LEGÍTIMO — é o cliente que ainda não operou —, e não
          falha. Mostrar erro aqui faria o começo normal do fluxo parecer defeito.
        */
        <span>
          Sua carteira de custódia é aberta na primeira operação. Nada a depositar ainda.
        </span>
      ) : networks.length === 0 ? (
        <span>
          Carteira criada, sem endereço de depósito ainda. Fale com o suporte.
        </span>
      ) : (
        <>
          <p className="custody__hint">
            Cada rede tem o seu endereço. Deposite SEMPRE na rede do endereço que você
            copiar — cripto enviada em outra rede não volta.
          </p>
          {networks.map((network) => (
            <Network key={network.network} network={network} />
          ))}
        </>
      )}
    </section>
  );
}
